import BaseApiService from "./BaseApiService";

/**
 * Scheduler API Service
 *
 * Handles all scheduler and notification-related API calls including:
 * - Creating, editing, and deleting notifications
 * - Managing notification schedules
 * - Fetching exercise preferences
 * - Updating Firebase tokens
 *
 * @class SchedulerApiService
 * @extends BaseApiService
 */
class SchedulerApiService extends BaseApiService {
  /**
   * Fetches user's home data including notifications and schedules
   * @param {string} timezoneOffset - Timezone offset (e.g., "+02", "-05")
   * @param {string} language - Language code ('en', 'de')
   * @returns {Promise<Object>} Home data with notifications, schedules, and user info
   */
  async getHomeData(timezoneOffset, language) {
    const offset = timezoneOffset || this.getTimezoneOffset();
    const lang = language || "de";
    const response = await this.get(`/home/${offset}/${lang}`);
    return response;
  }

  /**
   * Fetches user's exercise preferences
   * @param {string} language - Language code ('en', 'de')
   * @returns {Promise<Object>} Exercise preferences data
   */
  async getExercisePreferences(language) {
    const lang = language || "de";
    return await this.get(`/home/preferences/${lang}`);
  }

  /**
   * Creates a new notification
   * @param {Object} notificationData - Notification data
   * @param {string} notificationData.notificationDate - ISO date string
   * @param {string} notificationData.notificationTime - ISO time string
   * @param {string} notificationData.repeatInterval - Repeat interval ('NONE', 'DAILY', 'WEEKLY', etc.)
   * @param {string|null} notificationData.repeatPeriod - ISO date string for repeat end
   * @param {Array<number>} notificationData.preferenceIds - Array of exercise preference IDs
   * @returns {Promise<Object>} Created notification response
   */
  async createNotification(notificationData) {
    return await this.post("/home/notification", {
      notifications: [notificationData],
    });
  }

  /**
   * Edits an existing notification
   * @param {number} notificationId - ID of the notification to edit
   * @param {Object} notificationData - Updated notification data
   * @returns {Promise<Object>} Updated notification response
   */
  async editNotification(notificationId, notificationData) {
    return await this.post(`/home/notification/edit/${notificationId}`, {
      notifications: [notificationData],
    });
  }

  /**
   * Edits a notification schedule
   * @param {number} scheduleId - ID of the schedule to edit
   * @param {Object} notificationData - Updated notification data
   * @returns {Promise<Object>} Updated schedule response
   */
  async editSchedule(scheduleId, notificationData) {
    return await this.post(`/home/notification/schedule/edit/${scheduleId}`, {
      notifications: [notificationData],
    });
  }

  /**
   * Deletes a notification
   * @param {number} notificationId - ID of the notification to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteNotification(notificationId) {
    return await this.delete(`/home/notification/delete/${notificationId}`);
  }

  /**
   * Deletes a notification schedule
   * @param {number} scheduleId - ID of the schedule to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteSchedule(scheduleId) {
    return await this.post(`/home/notification/schedule/delete/${scheduleId}`);
  }

  /**
   * Deletes an entire notification series (all schedules for a notification)
   * @param {number} notificationId - ID of the notification series to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteNotificationSeries(notificationId) {
    return await this.delete(
      `/home/notification/series/delete/${notificationId}`
    );
  }

  /**
   * Deletes a schedule series
   * @param {number} scheduleId - ID of the schedule series to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteScheduleSeries(scheduleId) {
    return await this.delete(
      `/home/notification/schedule/series/delete/${scheduleId}`
    );
  }

  /**
   * Updates Firebase token for push notifications
   * @param {string|null} firebaseToken - Firebase FCM token
   * @param {string|null} safariSubscriberId - Safari push alert subscriber ID
   * @returns {Promise<Object>} Update confirmation
   */
  async updateFirebaseToken(firebaseToken = null, safariSubscriberId = null) {
    return await this.post("/home/update/firebase/token", {
      firebaseToken,
      safariSubscriberId,
    });
  }

  /**
   * Gets notification statistics and analytics
   * @param {string} [period] - Period for stats ('week', 'month', 'year')
   * @returns {Promise<Object>} Notification statistics
   */
  async getNotificationStats(period = "month") {
    return await this.get(`/home/stats?period=${period}`);
  }

  /**
   * Marks notification as completed/viewed
   * @param {number} notificationId - ID of the notification
   * @returns {Promise<Object>} Completion confirmation
   */
  async markNotificationCompleted(notificationId) {
    return await this.post(`/home/notification/${notificationId}/complete`);
  }

  /**
   * Gets available exercise types/categories
   * @returns {Promise<Array>} List of exercise types
   */
  async getExerciseTypes() {
    return await this.get("/home/exercise-types");
  }

  /**
   * Updates user's exercise preferences
   * @param {Array<number>} preferenceIds - Array of preference IDs
   * @returns {Promise<Object>} Update confirmation
   */
  async updateExercisePreferences(preferenceIds) {
    return await this.put("/home/preferences", { preferenceIds });
  }

  /**
   * Validates notification time to avoid conflicts
   * @param {string} notificationTime - ISO datetime string
   * @param {number} [excludeNotificationId] - ID to exclude from conflict check
   * @returns {Promise<Object>} Validation result
   */
  async validateNotificationTime(
    notificationTime,
    excludeNotificationId = null
  ) {
    const params = new URLSearchParams({ notificationTime });
    if (excludeNotificationId) {
      params.append("excludeId", excludeNotificationId.toString());
    }
    return await this.get(`/home/notification/validate?${params.toString()}`);
  }

  /**
   * Gets notification history for a specific period
   * @param {string} startDate - Start date (ISO string)
   * @param {string} endDate - End date (ISO string)
   * @returns {Promise<Array>} Notification history
   */
  async getNotificationHistory(startDate, endDate) {
    const params = new URLSearchParams({ startDate, endDate });
    return await this.get(`/home/notification/history?${params.toString()}`);
  }

  /**
   * Snoozes a notification for a specified duration
   * @param {number} notificationId - ID of the notification
   * @param {number} snoozeDurationMinutes - Duration in minutes
   * @returns {Promise<Object>} Snooze confirmation
   */
  async snoozeNotification(notificationId, snoozeDurationMinutes) {
    return await this.post(`/home/notification/${notificationId}/snooze`, {
      duration: snoozeDurationMinutes,
    });
  }

  /**
   * Gets upcoming notifications for the next specified hours
   * @param {number} [hours=24] - Number of hours to look ahead
   * @returns {Promise<Array>} Upcoming notifications
   */
  async getUpcomingNotifications(hours = 24) {
    return await this.get(`/home/notification/upcoming?hours=${hours}`);
  }

  // ========================================
  // Video Library Operations
  // ========================================

  /**
   * Gets all videos for video library
   * @param {string} language - Language code ('en', 'de')
   * @returns {Promise<Array>} List of all videos
   */
  async getLibraryVideos(language) {
    const lang = language || "de";
    return await this.get(`/home/videos/${lang}`);
  }

  /**
   * Gets videos for a specific library/category
   * @param {string} libraryId - Library/category ID
   * @returns {Promise<Array>} List of videos in library
   */
  async getVideosForLibrary(libraryId) {
    return await this.get(`/library/${libraryId}/videos`);
  }

  /**
   * Gets video by ID with full details
   * @param {string} videoId - Video ID
   * @param {boolean} isPhysioSource - Indicates if the source is physio
   * @returns {Promise<Object>} Video details
   */
  async getVideoById(videoId, isPhysioSource = false) {
    return await this.get(`/video/${videoId}`, {
      params: { isPhysioSource },
    });
  }

  async getVideosWatchedThisWeek() {
    return await this.get("/video/videosWatchedThisWeek");
  }

  /**
   * Records video play event
   * @param {string} videoId - Video ID
   * @param {Object} playData - Play event data
   * @param {number} playData.duration - Play duration in seconds
   * @param {number} playData.progress - Play progress percentage
   * @returns {Promise<Object>} Recording result
   */
  async recordVideoPlay(videoId, playData) {
    return await this.post(`/video/${videoId}/play`, {
      duration: playData.duration,
      progress: playData.progress,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Gets user's video watch history
   * @param {Object} options - Query options
   * @param {number} options.limit - Maximum number of items
   * @param {number} options.offset - Offset for pagination
   * @returns {Promise<Array>} User's watch history
   */
  async getVideoWatchHistory(options = {}) {
    const { limit = 20, offset = 0 } = options;
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    return await this.get(`/user/video-history?${params}`);
  }

  /**
   * Gets the Calendar token
   * @returns {Promise<String>} The Calendar token
   */
  async getCalendarToken() {
    return await this.get(`/ical/getToken`);
  }
}

// Create and export a singleton instance
const schedulerApiService = new SchedulerApiService();
export default schedulerApiService;
